import React, { useState } from 'react'
import { toast } from 'react-toastify'
import axios from 'axios'
import { useAuth } from '../Auth/AuthContext'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Divider from '@mui/material/Divider'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import SendIcon from '@mui/icons-material/Send'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import { messageContainsRejectedWord } from '../Constants'
import rejectPushImage from '../Assets/reject_push.jpeg'
import aiIcon from '../Assets/Icons/ai.png'

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 600 },
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    borderRadius: '8px',
    boxShadow: 24,
    p: 4,
    overflow: 'auto',
    fontFamily: 'Gill-Sans, sans-serif',
};

const MESSAGE_TYPES = [
    { value: 'general', label: 'General Message' },
    { value: 'venue_info', label: 'Venue Info' },
    { value: 'gig_cover', label: 'Gig Cover' },
]

export default function SendPush({ open, setOpen }) {

    const { currentUser, isDancerAdmin } = useAuth()

    const [messageType, setMessageType] = useState('general')
    const [body, setBody] = useState("")
    const [linkUrl, setLinkUrl] = useState("")
    const [sending, setSending] = useState(false)
    const [sendToAll, setSendToAll] = useState(false)
    const [sendToDJs, setSendToDJs] = useState(false)
    const [sendToDancers, setSendToDancers] = useState(isDancerAdmin)
    const [sendToSolos, setSendToSolos] = useState(false)
    const [sendToDuos, setSendToDuos] = useState(false)
    const [previewing, setPreviewing] = useState(false)
    const [previewData, setPreviewData] = useState(null)
    const [hrRejectOpen, setHrRejectOpen] = useState(false)
    const [rewriting, setRewriting] = useState(false)
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

    const getAuthHeaders = async () => {
        if (!currentUser) {
            throw new Error('Not authenticated')
        }
        const token = await currentUser.getIdToken()
        return { AuthToken: token }
    }

    // Dancer admins may only message dancers, regardless of checkbox state
    const buildCategories = () => isDancerAdmin
        ? { sendToAll: false, sendToDJs: false, sendToDancers: true, sendToSolos: false, sendToDuos: false }
        : { sendToAll, sendToDJs, sendToDancers, sendToSolos, sendToDuos }

    const handlePreview = async () => {
        if (!sendToAll && !sendToDJs && !sendToDancers && !sendToSolos && !sendToDuos) {
            toast.error("Please select at least one recipient category")
            return
        }

        setPreviewing(true)
        const categories = buildCategories()

        try {
            const headers = await getAuthHeaders()
            const res = await axios.post(
                `${process.env.REACT_APP_API}/notifications/preview-recipients`,
                { categories },
                { headers }
            )
            setPreviewData(res.data)
            console.log('Preview Recipients:', res.data)
            toast.success(`Would send to ${res.data.count} artists`)
        } catch (e) {
            console.log(e);
            toast.error("Failed to preview recipients")
        } finally {
            setPreviewing(false)
        }
    }

    const handleRewrite = async () => {
        if (!body.trim()) {
            toast.error("Please write a message first")
            return
        }

        setRewriting(true)
        try {
            const headers = await getAuthHeaders()
            const res = await axios.post(
                `${process.env.REACT_APP_API}/notifications/rewrite-message`,
                { body },
                { headers }
            )
            const rewritten = res.data?.body || ""
            if (!rewritten) {
                toast.error("AI returned an empty message")
                return
            }
            setBody(rewritten)
            toast.success("Message rewritten — feel free to edit before sending")
        } catch (e) {
            console.log(e)
            toast.error(e.response?.data?.error || "Failed to rewrite message")
        } finally {
            setRewriting(false)
        }
    }

    const normalizeLinkUrl = (url) => {
        const trimmed = url.trim()
        if (!trimmed) return ""
        return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    }

    const isValidLinkUrl = (url) => {
        try {
            new URL(normalizeLinkUrl(url))
            return true
        } catch {
            return false
        }
    }

    const handleSend = async () => {
        if (!body) {
            toast.error("Please add message")
        } else if (body.length > 150) {
            toast.error("Message too long")
        } else if (linkUrl.trim() && !isValidLinkUrl(linkUrl)) {
            toast.error("Please enter a valid link URL")
        } else if (messageContainsRejectedWord(body)) {
            setHrRejectOpen(true)
        } else if (!sendToAll && !sendToDJs && !sendToDancers && !sendToSolos && !sendToDuos) {
            toast.error("Please select at least one recipient category")
        } else {
            setSending(true)
            const categories = buildCategories()
            const payload = { body, categories, type: messageType }
            const normalizedLink = normalizeLinkUrl(linkUrl)
            if (normalizedLink) {
                payload.linkUrl = normalizedLink
            }
            try {
                const headers = await getAuthHeaders()
                await axios.post(
                    `${process.env.REACT_APP_API}/notifications/send-message`,
                    payload,
                    { headers }
                )
                setOpen(false)
                setBody("")
                setLinkUrl("")
                setMessageType('general')
                setSendToAll(false)
                setSendToDJs(false)
                setSendToDancers(isDancerAdmin)
                setSendToSolos(false)
                setSendToDuos(false)
                toast.success("Sent")
            } catch (e) {
                console.log(e);
                toast.error("Failed to send notification")
            } finally {
                setSending(false)
            }
        }
    }

    return (
        <>
        <Modal
            open={hrRejectOpen}
            onClose={() => setHrRejectOpen(false)}
            aria-labelledby="hr-reject-modal"
            sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: 420 },
                    bgcolor: 'background.paper',
                    borderRadius: '8px',
                    boxShadow: 24,
                    p: 3,
                    textAlign: 'center',
                    fontFamily: 'Gill-Sans, sans-serif',
                }}
            >
                <Box
                    component="img"
                    src={rejectPushImage}
                    alt="HR does not approve"
                    sx={{
                        width: '100%',
                        maxHeight: 280,
                        objectFit: 'contain',
                        borderRadius: '8px',
                        mb: 2,
                    }}
                />
                <Typography
                    id="hr-reject-modal"
                    variant="h6"
                    sx={{
                        fontFamily: 'railroad-gothic-atf, sans-serif',
                        color: '#272838',
                        letterSpacing: '1px',
                        mb: 2,
                    }}
                >
                    HR does not approve of this message
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => setHrRejectOpen(false)}
                    sx={{
                        backgroundColor: '#f47fdf',
                        color: 'white',
                        borderRadius: '8px',
                        fontFamily: 'Gill-Sans, sans-serif',
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: '#d965c7',
                        },
                    }}
                >
                    Edit message
                </Button>
            </Box>
        </Modal>

        <Modal
            open={open}
            onClose={() => setOpen(false)}
            aria-labelledby="send-notification-modal"
        >
            <Box sx={modalStyle}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <NotificationsActiveIcon sx={{ fontSize: 32, color: '#f47fdf' }} />
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            fontFamily: 'railroad-gothic-atf, sans-serif',
                            fontWeight: 400,
                            color: '#272838',
                            letterSpacing: '1px'
                        }}
                    >
                        Send Push Notification
                    </Typography>
                </Box>

                {/* Message Type Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            mb: 1,
                            fontWeight: 600,
                            color: '#272838',
                            fontFamily: 'Gill-Sans, sans-serif',
                            letterSpacing: '1px'
                        }}
                    >
                        Message Type
                    </Typography>
                    <FormControl fullWidth>
                        <Select
                            value={messageType}
                            onChange={(e) => setMessageType(e.target.value)}
                            sx={{
                                fontFamily: 'Gill-Sans, sans-serif',
                                letterSpacing: '1px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#d4d4d4',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#f47fdf',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#f47fdf',
                                },
                            }}
                        >
                            {MESSAGE_TYPES.map((type) => (
                                <MenuItem
                                    key={type.value}
                                    value={type.value}
                                    sx={{ fontFamily: 'Gill-Sans, sans-serif', letterSpacing: '1px' }}
                                >
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Message Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            mb: 1,
                            fontWeight: 600,
                            color: '#272838',
                            fontFamily: 'Gill-Sans, sans-serif',
                            letterSpacing: '1px'
                        }}
                    >
                        Message
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Enter your notification message..."
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontFamily: 'Gill-Sans, sans-serif',
                                letterSpacing: '1px',
                                '& fieldset': {
                                    borderColor: '#d4d4d4',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#f47fdf',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#f47fdf',
                                },
                            },
                        }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                        <Tooltip title="Rewrite with AI">
                            <span>
                                <IconButton
                                    onClick={handleRewrite}
                                    disabled={rewriting || !body.trim()}
                                    aria-label="Rewrite message with AI"
                                    size="small"
                                    sx={{
                                        border: '1px solid #f1f1f1',
                                        borderRadius: '8px',
                                        '&:hover': {
                                            borderColor: '#f47fdf',
                                            backgroundColor: 'rgba(244, 127, 223, 0.06)',
                                        },
                                    }}
                                >
                                    {rewriting ? (
                                        <CircularProgress size={18} sx={{ color: '#f47fdf' }} />
                                    ) : (
                                        <Box
                                            component="img"
                                            src={aiIcon}
                                            alt="AI rewrite"
                                            sx={{ width: 20, height: 20, display: 'block' }}
                                        />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Typography
                            variant="caption"
                            sx={{
                                color: body.length > 150 ? '#c23b22' : '#666',
                                fontWeight: body.length > 150 ? 600 : 400,
                                fontFamily: 'Gill-Sans, sans-serif',
                                letterSpacing: '1px'
                            }}
                        >
                            {body.length}/150 characters
                        </Typography>
                    </Box>
                </Box>

                {/* Link Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            mb: 1,
                            fontWeight: 600,
                            color: '#272838',
                            fontFamily: 'Gill-Sans, sans-serif',
                            letterSpacing: '1px'
                        }}
                    >
                        Link (optional)
                    </Typography>
                    <TextField
                        fullWidth
                        type="url"
                        placeholder="https://example.com"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontFamily: 'Gill-Sans, sans-serif',
                                letterSpacing: '1px',
                                '& fieldset': {
                                    borderColor: '#d4d4d4',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#f47fdf',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#f47fdf',
                                },
                            },
                        }}
                    />
                </Box>

                <Divider sx={{ my: 3, borderColor: '#f1f1f1' }} />

                {/* Recipients Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: '#272838',
                            fontFamily: 'Gill-Sans, sans-serif',
                            letterSpacing: '1px'
                        }}
                    >
                        Recipients
                    </Typography>
                    <FormGroup>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                            gap: 1
                        }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={sendToAll}
                                        disabled={isDancerAdmin}
                                        onChange={(e) => setSendToAll(e.target.checked)}
                                        sx={{
                                            color: '#f47fdf',
                                            '&.Mui-checked': {
                                                color: '#f47fdf',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontWeight: sendToAll ? 600 : 400,
                                            fontFamily: 'Gill-Sans, sans-serif',
                                            letterSpacing: '1px',
                                            color: '#272838'
                                        }}
                                    >
                                        Send to All
                                    </Typography>
                                }
                                sx={{
                                    backgroundColor: sendToAll ? 'rgba(244, 127, 223, 0.1)' : 'transparent',
                                    borderRadius: '8px',
                                    border: sendToAll ? '1px solid #f47fdf' : '1px solid #f1f1f1',
                                    px: 1,
                                    py: 0.5,
                                    m: 0,
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        backgroundColor: 'rgba(244, 127, 223, 0.05)',
                                        borderColor: '#f47fdf'
                                    }
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={sendToDJs}
                                        disabled={isDancerAdmin}
                                        onChange={(e) => setSendToDJs(e.target.checked)}
                                        sx={{
                                            color: '#f47fdf',
                                            '&.Mui-checked': {
                                                color: '#f47fdf',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontWeight: sendToDJs ? 600 : 400,
                                            fontFamily: 'Gill-Sans, sans-serif',
                                            letterSpacing: '1px',
                                            color: '#272838'
                                        }}
                                    >
                                        DJs
                                    </Typography>
                                }
                                sx={{
                                    backgroundColor: sendToDJs ? 'rgba(244, 127, 223, 0.1)' : 'transparent',
                                    borderRadius: '8px',
                                    border: sendToDJs ? '1px solid #f47fdf' : '1px solid #f1f1f1',
                                    px: 1,
                                    py: 0.5,
                                    m: 0,
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        backgroundColor: 'rgba(244, 127, 223, 0.05)',
                                        borderColor: '#f47fdf'
                                    }
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={sendToDancers}
                                        disabled={isDancerAdmin}
                                        onChange={(e) => setSendToDancers(e.target.checked)}
                                        sx={{
                                            color: '#f47fdf',
                                            '&.Mui-checked': {
                                                color: '#f47fdf',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontWeight: sendToDancers ? 600 : 400,
                                            fontFamily: 'Gill-Sans, sans-serif',
                                            letterSpacing: '1px',
                                            color: '#272838'
                                        }}
                                    >
                                        Dancers
                                    </Typography>
                                }
                                sx={{
                                    backgroundColor: sendToDancers ? 'rgba(244, 127, 223, 0.1)' : 'transparent',
                                    borderRadius: '8px',
                                    border: sendToDancers ? '1px solid #f47fdf' : '1px solid #f1f1f1',
                                    px: 1,
                                    py: 0.5,
                                    m: 0,
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        backgroundColor: 'rgba(244, 127, 223, 0.05)',
                                        borderColor: '#f47fdf'
                                    }
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={sendToSolos}
                                        disabled={isDancerAdmin}
                                        onChange={(e) => setSendToSolos(e.target.checked)}
                                        sx={{
                                            color: '#f47fdf',
                                            '&.Mui-checked': {
                                                color: '#f47fdf',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontWeight: sendToSolos ? 600 : 400,
                                            fontFamily: 'Gill-Sans, sans-serif',
                                            letterSpacing: '1px',
                                            color: '#272838'
                                        }}
                                    >
                                        Solos
                                    </Typography>
                                }
                                sx={{
                                    backgroundColor: sendToSolos ? 'rgba(244, 127, 223, 0.1)' : 'transparent',
                                    borderRadius: '8px',
                                    border: sendToSolos ? '1px solid #f47fdf' : '1px solid #f1f1f1',
                                    px: 1,
                                    py: 0.5,
                                    m: 0,
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        backgroundColor: 'rgba(244, 127, 223, 0.05)',
                                        borderColor: '#f47fdf'
                                    }
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={sendToDuos}
                                        disabled={isDancerAdmin}
                                        onChange={(e) => setSendToDuos(e.target.checked)}
                                        sx={{
                                            color: '#f47fdf',
                                            '&.Mui-checked': {
                                                color: '#f47fdf',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontWeight: sendToDuos ? 600 : 400,
                                            fontFamily: 'Gill-Sans, sans-serif',
                                            letterSpacing: '1px',
                                            color: '#272838'
                                        }}
                                    >
                                        Duos
                                    </Typography>
                                }
                                sx={{
                                    backgroundColor: sendToDuos ? 'rgba(244, 127, 223, 0.1)' : 'transparent',
                                    borderRadius: '8px',
                                    border: sendToDuos ? '1px solid #f47fdf' : '1px solid #f1f1f1',
                                    px: 1,
                                    py: 0.5,
                                    m: 0,
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        backgroundColor: 'rgba(244, 127, 223, 0.05)',
                                        borderColor: '#f47fdf'
                                    }
                                }}
                            />
                        </Box>
                    </FormGroup>
                </Box>

                {/* Preview Results (only on localhost) */}
                {isLocalhost && previewData && (
                    <Box sx={{
                        mb: 3,
                        p: 2,
                        backgroundColor: 'rgba(244, 127, 223, 0.05)',
                        borderRadius: '8px',
                        border: '1px solid #f47fdf'
                    }}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 1,
                                fontWeight: 600,
                                color: '#272838',
                                fontFamily: 'Gill-Sans, sans-serif',
                                letterSpacing: '1px'
                            }}
                        >
                            Preview: {previewData.count} Recipients
                        </Typography>
                        <Box sx={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                                width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: '#f1f1f1',
                                borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#f47fdf',
                                borderRadius: '4px',
                            },
                        }}>
                            {previewData.artists.map((artist, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        py: 0.5,
                                        borderBottom: index < previewData.artists.length - 1 ? '1px solid #f1f1f1' : 'none'
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: 'Gill-Sans, sans-serif',
                                            color: '#272838',
                                            fontSize: '13px'
                                        }}
                                    >
                                        {artist.name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {artist.categories.solo && <Typography variant="caption" sx={{
                                            backgroundColor: '#88ABFD',
                                            px: 0.5,
                                            py: 0.25,
                                            borderRadius: '4px',
                                            fontSize: '10px',
                                            fontFamily: 'Gill-Sans, sans-serif'
                                        }}>Solo</Typography>}
                                        {artist.categories.duo && <Typography variant="caption" sx={{
                                            backgroundColor: '#ffde21',
                                            px: 0.5,
                                            py: 0.25,
                                            borderRadius: '4px',
                                            fontSize: '10px',
                                            fontFamily: 'Gill-Sans, sans-serif'
                                        }}>Duo</Typography>}
                                        {artist.categories.dj && <Typography variant="caption" sx={{
                                            backgroundColor: '#388004',
                                            color: 'white',
                                            px: 0.5,
                                            py: 0.25,
                                            borderRadius: '4px',
                                            fontSize: '10px',
                                            fontFamily: 'Gill-Sans, sans-serif'
                                        }}>DJ</Typography>}
                                        {artist.categories.dancer && <Typography variant="caption" sx={{
                                            backgroundColor: '#f47fdf',
                                            color: 'white',
                                            px: 0.5,
                                            py: 0.25,
                                            borderRadius: '4px',
                                            fontSize: '10px',
                                            fontFamily: 'Gill-Sans, sans-serif'
                                        }}>Dancer</Typography>}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setOpen(false)}
                        sx={{
                            color: '#f47fdf',
                            borderColor: '#f47fdf',
                            borderRadius: '8px',
                            fontFamily: 'Gill-Sans, sans-serif',
                            fontSize: '16px',
                            letterSpacing: '-0.5px',
                            padding: '10px 20px',
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: '#f47fdf',
                                backgroundColor: 'rgba(244, 127, 223, 0.04)',
                                boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px'
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    {isLocalhost && (
                        <Button
                            variant="outlined"
                            onClick={handlePreview}
                            disabled={previewing}
                            sx={{
                                color: '#f47fdf',
                                borderColor: '#f47fdf',
                                borderRadius: '8px',
                                fontFamily: 'Gill-Sans, sans-serif',
                                fontSize: '16px',
                                letterSpacing: '-0.5px',
                                padding: '10px 20px',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: '#f47fdf',
                                    backgroundColor: 'rgba(244, 127, 223, 0.04)',
                                    boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px'
                                },
                            }}
                        >
                            {previewing ? 'Loading...' : 'Preview Recipients'}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleSend}
                        disabled={sending}
                        endIcon={<SendIcon />}
                        sx={{
                            backgroundColor: '#f47fdf',
                            color: 'white',
                            borderRadius: '8px',
                            fontFamily: 'Gill-Sans, sans-serif',
                            fontSize: '16px',
                            letterSpacing: '-0.5px',
                            padding: '10px 20px',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#d965c7',
                                boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
                                cursor: 'pointer'
                            },
                            '&:disabled': {
                                backgroundColor: '#f5f5f5',
                                color: 'grey',
                                cursor: 'not-allowed'
                            },
                        }}
                    >
                        {sending ? 'Sending...' : 'Send Notification'}
                    </Button>
                </Box>
            </Box>
        </Modal>
        </>
    )
}
